import { useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/navbar";
import Footer from "./footer";

function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar />
      <div className="relative min-h-screen overflow-hidden">
        {/* Header Section with gradient background */}
        <section className="relative bg-black text-white py-32 overflow-hidden">
          <div className="absolute inset-0">
            {/* Dot Pattern */}
            <div
              className="absolute inset-0 opacity-50"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, #333 1px, transparent 0)`,
                backgroundSize: "24px 24px",
              }}
            />
            {/* Center Radial Gradient */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, rgba(255, 20, 147, 0.15) 0%, rgba(0, 0, 0, 0) 100%)",
              }}
            />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="container mx-auto px-4 relative z-10"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-center mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl text-center text-gray-400">
              This Privacy Policy explains how your personal information is
              collected, used, and shared when you visit or use lexi.tips (the
              &quot;Site&quot;).
            </p>
          </motion.div>
        </section>

        {/* Content Section */}
        <section className="relative py-24 bg-white">
          <div className="absolute inset-0">
            {/* Dot Pattern */}
            <div
              className="absolute inset-0 opacity-50"
              style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, #e5e5e5 1px, transparent 0)`,
                backgroundSize: "24px 24px",
              }}
            />
            {/* Center Radial Gradient */}
            <div
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px]"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, rgba(255, 20, 147, 0.1) 0%, rgba(255, 255, 255, 0) 100%)",
              }}
            />
          </div>

          <div className="container mx-auto px-4 relative">
            <div className="max-w-4xl mx-auto space-y-12">
              {/* Information Collection Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">
                  Personal Information We Collect
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  When you visit lexi.tips, we automatically collect certain
                  details about your device, such as browser type, IP address,
                  time zone, and cookies installed on your device. We also
                  collect information about the pages you view, how you arrived
                  at our site, and how you interact with it. This is referred to
                  as Device Information.
                </p>

                <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                  <h3 className="text-xl font-semibold mb-4">
                    Collection Methods
                  </h3>
                  <ul className="space-y-4 text-gray-600">
                    <li className="flex items-start gap-3">
                      <span className="text-brand-pink">•</span>
                      <span>
                        <strong>Device Information:</strong> We gather Device
                        Information using technologies including cookies, log
                        files, tags, pixels, and tracking tools such as Facebook
                        Pixel and Google Ads.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-brand-pink">•</span>
                      <span>
                        <strong>Subscription Information:</strong> When you
                        subscribe or attempt to subscribe through our site, we
                        collect personal details such as your name, email,
                        billing address, and payment details. This is referred
                        to as Order Information.
                      </span>
                    </li>
                  </ul>
                </div>

                <p className="text-gray-600 leading-relaxed">
                  Personal Information in this policy refers to both Device
                  Information and Order Information.
                </p>
              </motion.div>

              {/* How We Use Your Information - New Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">
                  How We Use Your Information
                </h2>
                <p>
                  Order Information is used to fulfill your subscription,
                  process payments, communicate with you, screen for fraud, and
                  provide you with relevant updates or offers based on your
                  preferences.
                </p>
                <p>
                  Device Information helps us improve and secure the website,
                  analyse user behavior, and evaluate our marketing campaigns.
                </p>
              </motion.div>

              {/* How We Use Your Information - New Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">
                  Sharing Your Personal Information
                </h2>
                <p className="">
                  We may share your information with third-party service
                  providers like Google Analytics to better understand user
                  behavior. You can find out how Google uses your data at
                  www.google.com/policies/privacy and opt-out at
                  tools.google.com/dlpage/gaoptout.
                </p>
                <p>
                  We may also share information to comply with legal
                  requirements or to protect our rights.
                </p>
              </motion.div>

              {/* Behavioral Advertising - New Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">Behavioral Advertising</h2>
                <p className="text-gray-600 leading-relaxed">
                  We may use your information to provide targeted advertising
                  through platforms like Facebook, Google, and Bing. You can opt
                  out of targeted ads through the relevant ad settings pages on
                  each platform.
                </p>

                <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                  <h3 className="text-xl font-semibold mb-4">
                    You can opt out of some targeted advertising platforms
                    below:
                  </h3>
                  <ul className="space-y-4 text-gray-600">
                    <li className="flex items-start gap-3">
                      <span className="text-brand-pink">•</span>
                      <span>
                        Facebook:&nbsp;
                        <a
                          href="https://www.facebook.com/settings/?tab=ads"
                          className="text-brand-pink hover:underline"
                        >
                          https://www.facebook.com/settings/?tab=ads
                        </a>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-brand-pink">•</span>
                      <span>
                        Google:&nbsp;
                        <a
                          href="https://www.google.com/settings/ads/anonymous"
                          className="text-brand-pink hover:underline"
                        >
                          https://www.google.com/settings/ads/anonymous
                        </a>
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-brand-pink">•</span>
                      <span>
                        Bing:&nbsp;
                        <a
                          href="https://advertise.bingads.microsoft.com/en-us/resources/policies/personalized-ads"
                          className="text-brand-pink hover:underline"
                        >
                          https://advertise.bingads.microsoft.com/en-us/resources/policies/personalized-ads
                        </a>
                      </span>
                    </li>
                  </ul>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">Do not track</h2>
                <p className="text-gray-600 leading-relaxed">
                  We do not change our data collection practices in response to
                  Do Not Track signals.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">Data Retention</h2>
                <p className="text-gray-600 leading-relaxed">
                  We keep your subscription details on record unless you request
                  their deletion.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">Policy Updates</h2>
                <p className="text-gray-600 leading-relaxed">
                  We may occasionally update this policy to reflect operational,
                  legal, or regulatory changes.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">Minors</h2>
                <p className="text-gray-600 leading-relaxed">
                  This site is not intended for individuals under the age of 18.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">Copyright</h2>
                <p className="text-gray-600 leading-relaxed">
                  &copy; {new Date().getFullYear()} Lexi.Tips. All rights reserved. No part of this
                  site may be copied, reproduced, or stored in any format
                  without prior written permission. Downloading content for
                  personal, non-commercial use is permitted. Any unauthorized
                  commercial use is prohibited.
                </p>
                <p>
                  All product names, logos, and brands are property of their
                  respective owners.
                </p>
              </motion.div>

              {/* Contact Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="space-y-6"
              >
                <h2 className="text-3xl font-bold">Contact Us</h2>
                <p className="text-gray-600">
                  For questions about this Privacy Policy or to make a
                  complaint, contact us at: admin@lexi.tips
                </p>
              </motion.div>

              {/* Disclaimer Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="bg-black text-white p-8 rounded-xl relative overflow-hidden"
              >
                <div className="absolute inset-0 opacity-50">
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `radial-gradient(circle at 1px 1px, #333 1px, transparent 0)`,
                      backgroundSize: "24px 24px",
                    }}
                  />
                </div>
                <div className="relative z-10">
                  <h2 className="text-2xl font-bold mb-4">
                    Sports Betting Disclaimer
                  </h2>
                  <p className="text-gray-400">
                    Lexi.tips is a tipping information service only. We are not
                    a bookmaking or wagering site and we do not accept or place
                    bets. Our content is for entertainment and recreational
                    purposes only. Gambling involves risk. Only gamble with
                    money you can afford to lose. If you need support, please
                    contact 1800&nbsp;858&nbsp;858 or your local state gambling
                    helpline.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
}

export default PrivacyPolicy;
